// 190 ROUND 6 [W] (ruled 2026-09-16) — THE STRIP, ONCE. The seven counters
// have THREE HOSTS and THREE POPULATIONS, in Laurent's words:
//
//   matter     "a matter": left of the line follows the reader's filter, right
//              of the line is the matter's documents (round 5 [O], unchanged).
//   dashboard  "everything FILTERED to MY cases + filters — what did my
//              activity generate": his matters, narrowed by the dashboard's
//              own filters, all seven — the right three summed over the
//              matters the filtered rows sit on.
//   firm       the Invoices section at "Tous les dossiers": the firm, and AT
//              ALL TIMES UNFILTERED — "that's the firm overview". The list's
//              search and status filters do not touch it.
//
// THE CONSEQUENCE, STATED: the same seven words follow a filter on one screen
// and deliberately ignore it on another. That is his ruling, glanced, not an
// oversight — and `STRIP_HOSTS` is where it is written down, one row per host,
// so a fourth host later is a lookup here, not a fourth copy of the strip.
//
// WHAT LIVES HERE (moved out of TimeEntryTable.tsx, where the strip was welded
// in and the file is matter-scoped by construction — 151D's sticky floor,
// 150 §4's seven cards, the tooltip, the "—" gate — and written nowhere else):
//   CountersStrip     the sticky floor and the seven cards; takes its FIGURES
//                     from the host, never computes money.
//   stripFigures      the one reduction: the four left cards sum the served
//                     per-row `wc` (fees + costs, ex-VAT) over the host's
//                     rows; the three right cards sum the served `recap`
//                     members over the host's matters, per currency, each
//                     matter's own clamp kept.
//
// 190 ROUND 9 [AV] [RULED, Laurent 2026-09-17 — completes round 5's [O]]:
// THE BAR SEPARATES EARNED FROM CASH. "Left is about what is EARNED,
// consistent with the time entries, and right is about CASH FLOW, consistent
// with the PFs." THE RULE THAT MAKES IT CHECKABLE BY EYE: the strip's LEFT
// FOUR are the Récapitulatif's "honoraires + frais" column (fees grossed by
// the matter's costs ratio, EX-VAT — `costsAug` in caseFinancials.js, served
// per row as `wc`); its RIGHT THREE are the "honoraires + frais + TVA" column
// (the matter's documents as issued, VAT included — the served `recap`). One
// screen, two columns, the bar between them. Round 5 [O] had the left four
// VAT-inclusive for comparability; this supersedes that half: the left four
// are the work, and the work is not a cash figure. ENVOYÉ's label says where
// VAT enters ("Sent (with VAT)"); PAYÉ and DÛ, beyond it, inherit it.
//   useEntryFees      the POST /financials/entry-fees effect (the per-row
//                     money, server-handed — C.2) keyed by its own reduced
//                     body, so a host with a fresh array identity per render
//                     does not re-post.
// This is the union rule applied: TimeEntryTable declares the matter and the
// dashboard hosts (it is mounted by both), InvoiceView declares the firm host.
import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import MoneyStack from "./MoneyStack";
import { computeEntryFees } from "../api";
import { redEFCo, redEFMatter, redEFTableRow, redRoleRows } from "../lib/financialsClient";
import { fc } from "../utils";
import { isOwed } from "../lib/clientMatters.mjs"; // 191 E.2g
import { FS, Z } from "../lib/tokens";
import type { TFn } from "../lib/taxonomyTypes";

export type StripHost = "matter" | "dashboard" | "firm";

/* 190 round 11 [BA] (ruled) — THE BASIS BAR, ONE CONSTRUCTION. The strip's
   separator between its four earning cards and its three cash cards is a
   1-px stretch of `--br`; the three tables that carry the same seven columns
   (the two matter lists — matterListCfg.tsx — and Frais et honoraires —
   CostsView.tsx) draw the same bar as a cell border between FACTURÉ and
   ENVOYÉ, header and rows alike: the same width, the same colour, read from
   here by name. Nothing is invented for the tables; the strip's own div reads
   the same two values below. */
export const BASIS_BAR = { width: 1, color: "var(--br)" } as const;
export const BASIS_BAR_BORDER = `${String(BASIS_BAR.width)}px solid ${BASIS_BAR.color}`;

/* 190 round 8 [AK] [RULED, Laurent 2026-09-16 — "make it stick to bottom
   every time it appears"]: THE STRIP IS PINNED WHENEVER IT IS SHOWN, and the
   pin is a LAYOUT FACT, not a sticky bet. App.tsx renders one footer slot
   under the page scroller (`.main > .content + .contentFooter`, a flex row
   that takes no space while empty) and hands its DOM node down here; the
   plate PORTALS into it, so wherever a host mounts the strip — a matter's
   time section, the Dashboard's, the Invoices list scoped or firm-wide — it
   sits at the bottom of the viewport with the page scrolling above it.
   Round 7's [AE] had left the plate as the entry window's sticky floor,
   which is at the viewport's bottom only while the list overflows and the
   page is at its end; a short list put it under the last row and a filter
   moved it — "the strip jumps around everywhere". The sticky construction
   survives ONLY as the fallback of a host with no `.main` above it (none
   today): it is not a second design, it is what renders where the slot does
   not exist. */
export const ContentFooterSlot = React.createContext<HTMLElement | null>(null);
/** One row per host: the population in words (the tooltip on the three right-hand cards). */
export const STRIP_HOSTS: Readonly<Record<StripHost, Readonly<{ tipKey: string; tipFallback: string }>>> = {
  matter: { tipKey: "invoice.summary.matter_basis", tipFallback: "the matter's documents, as issued" },
  dashboard: { tipKey: "invoice.summary.dashboard_basis", tipFallback: "my matters within the filter, their documents" },
  firm: { tipKey: "invoice.summary.firm_basis", tipFallback: "the firm — every matter, unfiltered" },
};

/** The matter's served Récapitulatif members the right-hand cards read (GET /cases `recap`, matterRecap.js recapWire). */
export interface RecapWireSide {
  sent?: number | null; paid?: number | null; due?: number | null;
  foreign?: Readonly<Record<string, { sent?: number | null; paid?: number | null; due?: number | null }>> | null;
}
export interface StripFigures {
  /** the four left cards — null until the server's per-row money arrived */
  left: { unmarked: number; unbillable: number; billable: number; invoiced: number } | null;
  /** the three right cards — null where the host's matters carry no readable recap (absent is never zero) */
  right: { sent: number; paid: number; due: number; foreign: Record<string, { sent: number; paid: number; due: number }> | null } | null;
}
type RowLike = Readonly<{ id?: number | null; invoiceId?: number | null; mark?: string | null; billabilityMark?: string | null }>;
const markOf = (e: RowLike): string => e.mark ?? e.billabilityMark ?? "unmarked";

/** THE ONE REDUCTION. `rows` are the host's population (filtered or not — the host's declaration);
 *  `feeCalc` the served per-row money keyed by row id; `matters` the rows' matters (their `recap`). */
export function stripFigures(
  rows: readonly RowLike[],
  feeCalc: Readonly<Record<string, { wc?: number | null } | undefined>> | null,
  matters: readonly unknown[],
): StripFigures {
  return { left: leftFigures(rows, feeCalc), right: rightFigures(matters) };
}
/** 213B §2 — a served figure as the Amount cell SHOWS it: `fc` prints two decimals (half away from zero), so the
 *  sums a person reads are taken over these cents, never over the raw doubles. The drive found the raw sum a cent
 *  apart from its cells (ten rows: 1 178,10 € against 1 178,11 € — 45 min × 150 €/h × 1,19 = 133,875 lands on a
 *  half-cent); D.8 ruled the ladder in cents for the same reason (209 §3).
 *  214 addendum 1 §12 [RULED 55] — the definition MOVED to src/lib/shownCents.mjs, which the server's recap, costs
 *  line and activity line read too (caseFinancials.js): one expression for the strip's « Facturé » and the
 *  Récapitulatif's, so the two cannot read a cent apart over the same rows (Laurent's screen, 03/10). */
import { shownCents } from "../lib/shownCents.mjs";
/** the four left cards: the served per-row fees + costs (`wc`, ex-VAT — [AV]), partitioned by invoiceId / mark over
 *  fields the rows carry — summed in the cents the cells show (213B §2 [RULED 39]: the counter beside the tabs reads
 *  « the sum of what the Amount column shows », and the cards share this one reduction) */
function leftFigures(rows: readonly RowLike[], feeCalc: Readonly<Record<string, { wc?: number | null } | undefined>> | null): StripFigures["left"] {
  if (!feeCalc) return null;
  const s = { unmarked: 0, unbillable: 0, billable: 0, invoiced: 0 };
  for (const e of rows) {
    const ef = feeCalc[String(e.id)];
    const amt = ef && typeof ef.wc === "number" ? shownCents(ef.wc) : 0;
    if (e.invoiceId) { s.invoiced += amt; continue; }
    const m = markOf(e);
    if (m === "billable") s.billable += amt;
    else if (m === "unbillable") s.unbillable += amt;
    else s.unmarked += amt;
  }
  return { unmarked: s.unmarked / 100, unbillable: s.unbillable / 100, billable: s.billable / 100, invoiced: s.invoiced / 100 };
}
/** 213B §2 [RULED 39] — the money of the rows on screen, for the counter beside the tabs: the strip's
 *  own reduction, its four parts added — never a second computation, so the counter and the strip
 *  cannot disagree over the same rows. `rows` is `unknown`: the seam where the table's untyped rows
 *  are read (213 addendum 3 (b)). Null until the served money arrived — the counter then reads « — ». */
export function shownMoney(rows: unknown, feeCalc: Readonly<Record<string, { wc?: number | null } | undefined>> | null): number | null {
  const left = leftFigures(Array.isArray(rows) ? (rows as RowLike[]) : [], feeCalc);
  // the four parts added in cents (each is cent-exact; adding them as euros could land a double a hair off)
  return left ? (shownCents(left.unmarked) + shownCents(left.unbillable) + shownCents(left.billable) + shownCents(left.invoiced)) / 100 : null;
}
/** the three right cards: the served recap members summed over the host's matters, per currency */
function rightFigures(matters: readonly unknown[]): StripFigures["right"] {
  const recaps = matters
    .map((m) => (m as { recap?: RecapWireSide | null }).recap)
    .filter((r): r is RecapWireSide => !!r && typeof r === "object");
  if (!recaps.length || recaps.length !== matters.length) return null;
  const sum = { sent: 0, paid: 0, due: 0 };
  const foreign: Record<string, { sent: number; paid: number; due: number }> = {};
  for (const r of recaps) {
    sum.sent += r.sent ?? 0; sum.paid += r.paid ?? 0; sum.due += r.due ?? 0;
    for (const [code, f] of Object.entries(r.foreign ?? {})) {
      const tgt = foreign[code] ?? (foreign[code] = { sent: 0, paid: 0, due: 0 });
      tgt.sent += f.sent ?? 0; tgt.paid += f.paid ?? 0; tgt.due += f.due ?? 0;
    }
  }
  return { ...sum, foreign: Object.keys(foreign).length ? foreign : null };
}

/** The served per-row money, keyed by row id (C.2: POST /financials/entry-fees over the host's rows;
 *  `costs` asked for — [AV]/[AU]: the fees + costs figure and the ratio itself). The request is
 *  keyed by its reduced wire body — a host handing a fresh array every render does not re-post. */
/** One served row of the entry-fees `table` section (lib/entryFees.js): the rate, the amount,
 *  under `costs` the matter's costs ratio and the amount grossed by it (`wc`, ex-VAT — 190 round 9
 *  [AV]/[AU]; round 5's `ttc` is retired), and under `rates` whether the rate was STATED and what
 *  it would be. */
export interface EntryFeeRow { id?: number | null; er: number; amt: number | null; cr?: number; wc?: number | null; stated?: boolean; derived?: number }
export function useEntryFees(args: {
  entries: readonly unknown[];
  /** the row's matter — the host's own resolver (a matter-scoped host answers its one matter); typed as
   *  Array's own `map` callback parameter, so no named parameter sits in a function type here (183's rule) */
  caseOf: Parameters<unknown[]["map"]>[0];
  co: readonly unknown[];
  roleRows: readonly unknown[];
}): Readonly<Record<string, EntryFeeRow | undefined>> | null {
  const { entries, caseOf, co, roleRows } = args;
  const all: unknown[] = [...entries];
  const efMatters: unknown[] = [];
  const efmIdx = new Map<unknown, number>();
  const efmiOf = (m: unknown): number | null => {
    if (!m) return null;
    if (!efmIdx.has(m)) { efmIdx.set(m, efMatters.length); efMatters.push(redEFMatter(m)); }
    return efmIdx.get(m) ?? null;
  };
  const key = JSON.stringify({
    collaborators: (co as Parameters<typeof redEFCo>[0][]).map(redEFCo),
    roleRows: redRoleRows(roleRows as Parameters<typeof redRoleRows>[0]),
    table: {
      matters: efMatters,
      rates: true,
      costs: true,
      rows: all.map((e, i) => redEFTableRow(e, efmiOf(caseOf(e, i, all)))),
    },
  });
  const [feeCalc, setFeeCalc] = useState<Readonly<Record<string, EntryFeeRow>> | null>(null);
  const seq = useRef(0);
  useEffect(() => {
    const mine = ++seq.current;
    computeEntryFees(JSON.parse(key))
      .then((raw: unknown) => {
        if (seq.current !== mine) return;
        const r = raw as { table?: EntryFeeRow[] } | null;
        const byId: Record<string, EntryFeeRow> = {};
        for (const row of r?.table ?? []) if (row.id != null) byId[String(row.id)] = row;
        setFeeCalc(byId);
      })
      .catch(() => { if (seq.current === mine) setFeeCalc(null); });
  }, [key]);
  return feeCalc;
}

interface Props {
  readonly host: StripHost;
  readonly figures: StripFigures;
  /** the plate's colour — opaque, per surface (the card colour on the Dashboard, the standing shade elsewhere) */
  readonly background?: string;
  readonly t: TFn;
}

/* 151D §2(c)(e) → 190 round 8 [AK] — THE COUNTERS ARE THE PAGE'S FLOOR: the
   plate renders into App's footer slot (`ContentFooterSlot`, above), a flex
   row under the page scroller, so it sits at the viewport's bottom whenever
   it is shown and the page scrolls above it — no sticky arithmetic, no
   window to be the last child of. ONE pane, STRAIGHT corners: the seven
   cards flatten to plain cells on a single opaque plate. 150 §4: the seven
   values, the thin rule — after FACTURÉ since 190 round 5 [O]: four
   filter-based (or population-based) | three matter-based — and "—" until
   the server's figures arrive (absent is never zero). Without a slot (a host
   with no `.main` above it — none today) the plate is what it was: its own
   scroller's sticky floor at `bottom: 0`. */
export default function CountersStrip({ host, figures, background = "var(--bd)", t }: Props) {
  const slot = useContext(ContentFooterSlot);
  const tip = t(STRIP_HOSTS[host].tipKey, { fallback: STRIP_HOSTS[host].tipFallback });
  const card = (label: string, value: React.ReactNode, color: string, cardTip?: string) => (
    <div key={label} title={cardTip ?? undefined} style={{ flex: "1 1 120px", minWidth: 100, padding: "8px 14px" }}>
      <div style={{ fontSize: FS.s9, color: "var(--tm)", textTransform: "uppercase", letterSpacing: ".3px", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: FS.s16, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color }}>
        {value}
      </div>
    </div>
  );
  const l = figures.left;
  const r = figures.right;
  const money = (m: "sent" | "paid" | "due") =>
    r ? <MoneyStack home={r[m]} foreign={r.foreign ? Object.fromEntries(Object.entries(r.foreign).map(([k, f]) => [k, f[m]])) : null} /> : "—";
  const plate = (
    <div
      data-counters-strip={host}
      style={{
        ...(slot ? {} : { position: "sticky" as const, bottom: 0 }),
        zIndex: Z.counters,
        background,
        borderTop: "1px solid var(--br)",
        borderRadius: 0,
        boxShadow: "0 -6px 16px -8px var(--shd)",
        padding: "4px 8px 2px",
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "stretch",
      }}
    >
      {card(t("te.tab.unmarked", { fallback: "Unmarked" }), l ? fc(l.unmarked) : "—", "var(--gn)")}
      {card(t("te.tab.unbillable", { fallback: "Unbillable" }), l ? fc(l.unbillable) : "—", "var(--rd)")}
      {card(t("te.tab.billable", { fallback: "Billable" }), l ? fc(l.billable) : "—", "var(--am)")}
      {card(t("te.tab.invoiced", { fallback: "Invoiced" }), l ? fc(l.invoiced) : "—", "var(--ac)")}
      {/* the BASIS LINE (190 round 5 [O]): left of it the host's rows, right of it the host's matters */}
      <div aria-hidden style={{ width: BASIS_BAR.width, alignSelf: "stretch", background: BASIS_BAR.color }} />
      {/* [AV] — the label marks where VAT enters: a NEW key (105's rule — a changed text is a new
          key; `invoice.summary.sent` retired); Payé and Dû beyond it inherit it. */}
      {card(t("invoice.summary.sent_with_vat", { fallback: "Sent (with VAT)" }), money("sent"), "var(--ts)", tip)}
      {card(t("invoice.summary.paid", { fallback: "Paid" }), money("paid"), "var(--gn)", tip)}
      {/* 191 E.2g — the DÛ colour asks the half-cent rule: recapBlock's due is
          `max(0, sent − paid − credits)` over unrounded doubles, so a sub-cent
          residue (PF-2026/008's 0.0025) can be the whole of it. */}
      {card(t("invoice.summary.due", { fallback: "Due" }), money("due"), r && isOwed(r.due) ? "var(--rd)" : "var(--gn)", tip)}
    </div>
  );
  return slot ? createPortal(plate, slot) : plate;
}
